import { Icon, IconColor, IconID } from '@common/Icon/Icon';
import classNames from 'classnames';
import type { CSSProperties } from 'react';
import { Fragment, useState } from 'react';
import { BaseCollapsible, CollapsibleBackgroundTheme, CollapsibleTheme } from './BaseCollapsible';
import type { ContentItem } from '@models/data';

export interface CollapsibleProps {
  items: ContentItem[];
  activeTab?: number | null;
  onChange?: (newValue: number | null) => void;
  theme?: CollapsibleTheme;
  backgroundTheme?: CollapsibleBackgroundTheme;
  closeIcon?: IconID;
  openIcon?: IconID;
  titleIcon?: IconID;
  disableScrolling?: boolean;
  styles?: CSSProperties;
}

export const Collapsible = ({
  activeTab = null,
  items,
  onChange,
  theme = CollapsibleTheme.DARK,
  backgroundTheme = CollapsibleBackgroundTheme.NONE,
  closeIcon = IconID.PLUS,
  openIcon = IconID.MINUS,
  titleIcon,
  disableScrolling = false,
  styles = {},
}: CollapsibleProps) => {
  const [localActiveTab, setActiveTab] = useState<number | null>(activeTab); // null = all closed

  return (
    <>
      {items.map((item, index) => (
        <Fragment key={item.title}>
          <BaseCollapsible
            theme={theme}
            backgroundTheme={backgroundTheme}
            title={
              <div className="flex items-center">
                {titleIcon && (
                  <div className="max-w-[42px] mr-6" data-testid="collapsible-title-icon">
                    <Icon size={8} color={IconColor.WHITE} id={titleIcon} useSprite={false} />
                  </div>
                )}
                <div>
                  <h2 className="flex-grow font-bold text-base" dangerouslySetInnerHTML={{ __html: item.title }} />
                </div>
              </div>
            }
            isOpen={localActiveTab === index}
            openIcon={
              <Icon
                id={openIcon}
                size={6}
                primary={false}
                color={theme === CollapsibleTheme.DARK ? IconColor.WHITE : IconColor.DARK_GRAY}
                useSprite
              />
            }
            closeIcon={
              <Icon
                id={closeIcon}
                size={6}
                primary={false}
                color={theme === CollapsibleTheme.DARK ? IconColor.WHITE : IconColor.DARK_GRAY}
                useSprite
              />
            }
            onOpen={() => {
              if (index === localActiveTab) {
                setActiveTab(null);
                if (onChange) {
                  onChange(null);
                }
                return;
              }
              setActiveTab(index);

              if (onChange) {
                onChange(index);
              }
            }}
            disableScrolling={disableScrolling}
            styles={styles}
          >
            {item.content}
          </BaseCollapsible>
          {/* alternativeContent (e.g. summary of the collapsibles content) is shown (when provided)
              below a collapsible when it is in collapsed state */}
          {item.alternativeContent && localActiveTab !== index && (
            <div className={classNames(backgroundTheme === CollapsibleBackgroundTheme.LIGHT && 'bg-gray-50')}>
              {item.alternativeContent}
            </div>
          )}
        </Fragment>
      ))}
    </>
  );
};
